import React, { Component } from 'react';
import CSSModules from 'react-css-modules'
import ReactTooltip from 'react-tooltip'
import { Link as ScrollLink } from 'react-scroll'
import {Row, Col} from 'react-flexbox-grid'
import config from '../../config'
import container from '../../helpers/container'
import {
  ContainerDivs,
  Works,
  Link,
  LinkIcon,
  GetStartedInput,
  ScreenedCompanies,
  Grade,
  Changelog,
  ScrollToTop,
  AutocompleteSearch,
} from '../../components'
import Styles from './HowItWorks.css'


@CSSModules(Styles)
export default class HowItWorks extends Component {
  render() {
    const title = 'How it works'
    const description = 'Deforestation Free Funds tracks thousands of mutual funds commonly found in retirement plans.'
    const url = config.domain + config.routes.howitworks

    const sriIcon = require( '../../../static/images/sri.png' )
    const ussifIcon = require( '../../../static/images/ussif-icon.png' )

    //
    return (
      <div styleName='page' id='top'>
        {container.helmet(title, description, url)}

        <ContainerDivs>
          <Row styleName='title-nav-row'>
            <Col xs={12} sm={8} styleName='title-nav-title'>
              <h1 styleName='title'>
                How it works
              </h1>
              <p styleName='subtitle'>
                Getting started,
                methodology,
                and deforestation-risk company lists
              </p>
              <p styleName='lede'>
                Deforestation Free Funds sources financial data from
                Morningstar, an investment research company. We use
                Morningstar’s mutual fund and stock data to analyze
                funds and find investments in deforestation-risk companies.
                Our database covers thousands of the most popular mutual
                funds, the type of investments most common in retirement
                plans like employer-offered 401(k)s.
              </p>
            </Col>

            <Col xs={12} sm={4} styleName='title-nav-nav'>
              <nav styleName='page-nav'>
                <div styleName='page-nav-title'>
                  Contents
                </div>
                {
                  [
                    ['Getting started','getting-started'],
                    ['Deforestation-risk companies','companies'],
                    ['Mutual fund grades','deforestation-grades'],
                    // ['Fund managers','fund-manager-ratings'],
                    ['Spreadsheet','spreadsheet'],
                  ].map((e, i) => {
                    return <ScrollLink
                      key={'nav-scroll-link-'+i}
                      to={e[1]}
                      spy
                      smooth
                      duration={500}
                      title={e[0]}
                    >
                      <div styleName='page-nav-btn'>
                        {e[0]}
                      </div>
                    </ScrollLink>
                  })
                }
              </nav>
            </Col>
          </Row>
        </ContainerDivs>

        <Works/>

        <ContainerDivs>
          <section styleName='methodology-section'>
            <div styleName='anchor' id='getting-started'/>
            <h2 styleName='section-title'>
              Getting started
            </h2>

            <h3 styleName='answer-title'>
              Finding your funds
            </h3>
            <div styleName='answer-info'>
              <p>
                We source data on
                companies and mutual funds from financial services provider
                Morningstar. Our database
                covers thousands of U.S. open-end mutual funds and
                exchange-traded funds (ETFs),
                some of the most common funds held in
                401(k)s, 403(b)s, and other retirement plans.
              </p>
              <p>
                Search for funds by name, ticker, or asset
                manager. Use the search bar below, or <Link
                  to={config.routes.clean}
                  title='Search funds'
                  xstrong
                >use the search page</Link> to do more advanced searches, 
                such as filtering funds by investing style or 
                asset manager. When you find a fund you're looking for,
                click it to see the full results.
              </p>

              <div styleName='search-bar-wrap'>
                <AutocompleteSearch />
              </div>

              <br/>

              <p>
                We don't have everything in our database &mdash; we
                only analyze U.S. mutual funds and ETFs 
                that are at least 50% invested in stock holdings,
                and our data license restricts us to
                displaying only a few thousand portfolios.
              </p>
              <p>
                Looking for your favorite fund and
                can&rsquo;t find it? With more resources,
                we could include more funds &mdash; <Link
                  to={config.links.donate}
                  title='Donate to As You Sow'
                  xstrong
                >make a gift today to make a difference.</Link>
              </p>

              <div styleName='sidebar'>
                <div styleName='sidebar-title'>
                  <LinkIcon faclass='fa-info-circle' nospace/>
                  &nbsp;
                  <em>Our research universe</em>
                </div>
                <div styleName='sidebar-info'>
                  <p>
                    Our database covers open-end mutual funds and
                    exchange-traded funds domiciled in the U.S.
                    Currently, we don’t have data on funds domiciled
                    outside the U.S. The fund ratings are based on
                    tracking stock investments in publicly-traded
                    companies. Currently, we don’t have ratings for
                    fixed income funds. We exclude funds with less
                    than 50% of fund assets in equity holdings
                    from the analysis universe.
                  </p>
                </div>
              </div>
            </div>
            <ScrollToTop />
          </section>

          <section styleName='methodology-section'>
            <div styleName='anchor' id='companies'/>
            <h2 styleName='section-title'>
              Deforestation-risk companies
            </h2>
            <div styleName='answer-info'>
              <p>
                The Deforestation Free Funds list is comprised of 380 publicly listed companies, compiled from independent research as well as from existing resources within the civil society research and investor advocacy communities on forest-risk companies, including <Link to={config.links.fAndf} title='Forests & Finance' xstrong >Forests & Finance</Link>, <Link to={config.links.forest500} title='Forest500' xstrong >Forest500</Link>, <Link to={config.links.cdpForests} title='CDP Forests' xstrong >CDP Forests</Link>, <Link to={config.links.supplychange} title='Supply Change.org' xstrong >Supply Change.org</Link> and <Link to={config.links.spott} title='ZSL SPOTT' xstrong >ZSL SPOTT</Link>. Every company on the Deforestation Free Funds list may be considered at risk for exposure to tropical deforestation, whether directly through production and trading (Commodity producers and traders); through supply chain links (Major consumer brands); or through financing of producers and traders (Forest-risk banks and lenders). Where a company is a parent or a subsidiary of a privately held company, only the publicly listed company is named.
              </p>
              <p>
                Most companies on the list were sourced through existing resources within the civil society research and investor advocacy communities, including <Link to={config.links.fAndf} title='Forests & Finance' xstrong >Forests & Finance</Link>, <Link to={config.links.forest500} title='Forest500' xstrong >Forest500</Link>, <Link to={config.links.cdpForests} title='CDP Forests' xstrong >CDP Forests</Link>, <Link to={config.links.supplychange} title='Supply Change.org' xstrong >Supply Change.org</Link> and <Link to={config.links.spott} title='ZSL SPOTT' xstrong >ZSL SPOTT</Link>.
              </p>
              <p>
                <strong>Relative criteria:</strong> a significant percentage of company revenues are engaged in that deforestation-risk business sector. An example is Equatorial Palm Oil, which has a significant percentage of company revenues engaged in palm oil production.
              </p>
              <p>
                <strong>Absolute criteria:</strong> the percentage of company revenues engaged in that deforestation-risk business sector is not significant, but due to the large size of the company, the total amount of company revenue engaged in that deforestation-risk business sector is significant. An example is Amazon.com, which is a major consumer brand engaged in all six commodity sectors analyzed (palm oil, paper/pulp, rubber, timber, cattle, and soy). While the percentage of Amazon's company revenues engaged in these sectors is not significant, the sheer size of Amazon's operations makes the total amount of revenue significant.
              </p>
              <p>
                <strong>Policy criteria:</strong> the company has no corporate policies to manage and mitigate risk in the deforestation-risk business sectors it's engaged in (i.e. does not have a No Deforestation policy, or has gaps in achieving full implementation of said policy).
              </p>
            </div>
            <ScreenedCompanies />
          </section>

          <section styleName='methodology-section'>
            <div styleName='anchor' id='deforestation-grades'/>
            <h2 styleName='section-title'>
              Mutual fund grades
            </h2>
            <h3 styleName='answer-title'>
              Calculating exposure to deforestation-risk companies
            </h3>
            <div styleName='answer-info'>
              <p>
                Mutual funds can have a varying number of holdings,
                from less than one hundred to several thousand. For
                each fund, we calculate the total number, dollar
                amount, and percentage of the fund’s assets that are
                invested in those companies.
              </p>
              <p>
                We only look for direct stock holdings in
                deforestation-risk companies. That means that
                holdings that are not stocks, like cash holdings or
                bonds, are not rated. In the fund's investment
                profile, we show a "Percent Rated" metric,
                equivalent to the percent of the fund that is
                invested in stocks. The higher a fund&rsquo;s Percent
                Rated value, the more holdings we were able to examine.
                A fund with a lower Percent Rated value may have
                hidden deforestation-risk investments that our tool
                cannot account for, in the form of bond holdings
                or other asset types.
              </p>
              <div styleName='sidebar'>
                <div styleName='sidebar-title'>
                  <LinkIcon faclass='fa-info-circle' nospace/>
                  &nbsp;
                  <em>Measuring long holdings only</em>
                </div>
                <div styleName='sidebar-info'>
                  <p>
                    When calculating a fund's exposure to a screen list,
                    we only consider the portfolio's long investments,
                    ignoring any shorts or liabilities. The formula for
                    holding weight is <em>holding market value divided
                      by the sum of the market value for all long
                      holdings in the portfolio.</em>
                  </p>
                </div>
              </div>
            </div>

            <h3 styleName='answer-title'>
              Deforestation grades
            </h3>
            <div styleName='answer-info'>
              <p>
                Based on the fund's deforestation exposure, it earns a
                deforestation grade.
              </p>
              <div styleName='risk-bullets'>
                <div styleName='risk-bullet'>
                  <Grade grade={1} large />
                  <p styleName='screen-icon-label'>
                    No holdings flagged for our deforestation screens. Assigned a grade of A.
                  </p>
                </div>
                <div styleName='risk-bullet'>
                  <Grade grade={2} large />
                  <p styleName='screen-icon-label'>
                    Fund is invested in deforestation-risk banks and lenders
                    and/or major consumer brands, but no holdings flagged
                    for our deforestation-risk agricultural commodity
                      producer/trader screen. Assigned a grade of B.
                  </p>
                </div>
                <div styleName='risk-bullet'>
                  <Grade grade={3} large />
                  <p styleName='screen-icon-label'>
                    Fund is invested in deforestation-risk agricultural
                    commodity producer/traders, below the threshold of 0.5%.
                    Assigned a grade of C.
                  </p>
                </div>
                <div styleName='risk-bullet'>
                  <Grade grade={4} large />
                  <p styleName='screen-icon-label'>
                    Fund is invested in deforestation-risk agricultural
                    commodity producer/traders, above the threshold of
                    0.5% and below 1.5%.
                    Assigned a grade of D.
                  </p>
                </div>
                <div styleName='risk-bullet'>
                <Grade grade={5} large />
                <p styleName='screen-icon-label'>
                  Fund is invested in deforestation-risk agricultural
                  commodity producer/traders, above the threshold of 1.5%.
                  Assigned a grade of F.
                </p>
                </div>
              </div>
              <p>
                If a fund owns some of the companies we screen for, but
                has a proven track record of engaging the companies it
                owns by taking actions such as filing shareholder
                resolutions on deforestation or similar environmental
                issues, it earns a special acknowledgement as a “known
                sustainability engager”, instead of a letter grade.
              </p>
              <div styleName='risk-bullets'>
                <div styleName='risk-bullet'>
                  <Grade grade={1.5} large />
                  <p styleName='screen-icon-label'>
                    “Known sustainability engager” grade. Fund is offered
                    by an asset manager that frequently engages companies 
                    it invests in to push for better behavior using the
                    shareholder resolution process.
                  </p>
                </div>
              </div>
            </div>

            <h3 styleName='answer-title'>
              Details on deforestation exposure
            </h3>
            <div styleName='answer-info'>
              <p>
                Along with the deforestation grade, we show details on the
                fund portfolio's flagged holdings.
              </p>
              <p>
                The number of flagged holdings, the amount invested by the fund,
                and the percent of the fund's portfolio those holdings make up
                are shown. The same figures are available for the
                various sub-categories, and for each individual flagged holding.
              </p>
            </div>

            <h3 styleName='answer-title'>
              Sustainable funds
            </h3>
            <div styleName='answer-info'>
              <img
                styleName='sri'
                src={sriIcon}
                alt='Sustainably-mandated  fund'
                data-multiline
                data-effect='solid'
                data-html
                data-for='sri-icons-tooltip'
                data-place='bottom'
                data-tip="<div style='max-width:200px;'>Designated a sustainably-mandated fund by Morningstar. Sustainably-mandated funds consider issues like the environment and human rights.</div>"
              />
              <ReactTooltip id='sri-icons-tooltip'/>
              <p>
                We use Morningstar's "sustainability mandate" indicator
                to determine which funds are displayed as
                sustainably-mandated. Sustainably-mandated funds make
                investment decisions based on issues like environmental
                responsibility, human rights, or religious views.
                A sustainably-mandated fund may take a proactive stance
                by selectively investing in, for example,
                environmentally-friendly companies, or firms with good
                employee relations. They may also avoid investing in
                companies involved in promoting alcohol, tobacco, or
                firearms, or in the defense industry. Look for this
                symbol to find funds that are designated
                sustainably-mandated.
              </p>
            </div>

            <h3 styleName='answer-title'>
              Sustainability engagers
            </h3>
            <div styleName='answer-info'>
              <Grade grade={1.5} large />

              <p>
                If you see this symbol, it means that fund is a known
                sustainability engager.
              </p>
              <p>
                Some sustainability-mandated funds actively engage the companies they own. Indeed, efforts by sustainability-focused investors whose funds are found on this site have been crucial to driving positive change in the palm oil sector. A number of fund families are <strong>known sustainability engagers</strong>, and have taken actions such as filing <Link xstrong to={config.links.aysvote} title={'Vote Your Shares | As You Sow'}>shareholder resolutions</Link> asking for policy changes. Those families deserve special acknowledgement - often, they are using their investments to make sure they have a voice at the table to call for real improvements in company behavior. We used <Link xstrong to={config.links.ceres} title={'Ceres Shareholder Resolution Database'}>data collected by Ceres</Link> to identify fund families that have filed shareholder resolutions on deforestation, climate change, or other environmental issues. If a fund owns companies we screen for, but the family is a known filer of deforestation or environmental-related shareholder resolutions, it earns a special engagement score. <Link xstrong to={'#7'} title={'FAQs | Deforestation Free Funds'}>Read more about our deforestation grades.</Link>
              </p>
              <p>
                Are you a fund manager that has filed a deforestation-related
                shareholder resolution, or otherwise engage the companies in
                your portfolio on sustainability issues? <Link
                  to={config.links.contact}
                  title='Contact As You Sow'
                  xstrong
                >Get in touch - we’d love to hear from you.</Link>
              </p>
            </div>

            <h3 styleName='answer-title'>
              The Sustainable Investment Forum
            </h3>
            <div styleName='answer-info'>
              <img
                styleName='ussif'
                src={ussifIcon}
                alt='Member of US-SIF, the Forum for Sustainable and Responsible Investment'
                data-multiline
                data-effect='solid'
                data-html
                data-for='ussif-icons-tooltip'
                data-place='bottom'
                data-tip="<div style='max-width:200px;'>Member of US-SIF, the Forum for Sustainable and Responsible Investment</div>"
              />
              <ReactTooltip id='ussif-icons-tooltip'/>
              <p>
                The Forum for Sustainable and Responsible Investment
                is a group advancing sustainable, responsible, and impact
                investing. Asset managers who are members of US-SIF often
                have policies to exclude or restrict investments in
                companies with high deforestation risk.
                Look for this symbol to find funds
                that are members of US-SIF.
              </p>
            </div>

            <h3 styleName='answer-title'>
              #DefundDeforestation toolkit
            </h3>
            <div styleName='answer-info'>
              <p>
                When you're done looking up funds and finding the data you
                need, what's next? You can learn how to make a change and
                move your money with our <Link
                  to={config.routes.toolkit}
                  xstrong
                  title='#DefundDeforestation toolkit'
                >#DefundDeforestation toolkit</Link>. Whether you’re an
                  individual investor or if your investments are in
                  your employer-sponsored plan at work, our step-by-step
                  toolkit can help. There's an in-depth guide to
                  responsible investing, links to external resources,
                  a sample letter to send to your employer 401(k) manager,
                  and more &mdash; everything you need to make a change
                  and get started investing your money deforestation free.
              </p>
            </div>

            <ScrollToTop />
          </section>

          { /* CHANGELOG */ }
          <section styleName='methodology-section'>
            <div styleName='anchor' id='spreadsheet'/>
            <h2 styleName='section-title'>
              Spreadsheet
            </h2>
            <Changelog />
            <ScrollToTop />
          </section>

        </ContainerDivs>
        <br/>
        <br/>
        <br/>

        <GetStartedInput />
      </div>
    )
  }
}
